import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/83c31d00f47a619d4f0a515f0b3bc954/629dc85fc734a1771685601969667fb4.png" 
                alt="HP Inc Consulting Services Logo" 
                className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '-0.02em' }}>
                HP Inc Consulting Services
              </h1>
              <p className="text-xs font-semibold text-orange-600 tracking-wide">
                Safety • Environmental • Coatings
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`font-medium transition-colors hover:text-orange-600 ${
                isActive('/') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center space-x-1 font-medium text-gray-700 hover:text-orange-600 transition-colors"
                onMouseEnter={() => setActiveDropdown('services')}
              >
                <span>Services</span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              <div 
                className={`absolute top-full left-0 w-64 bg-white shadow-xl rounded-lg border border-gray-200 py-2 transition-all duration-200 ${
                  activeDropdown === 'services' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
                onMouseEnter={() => setActiveDropdown('services')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link 
                  to="/safety-staffing" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-center space-x-3">
                    <i className="ri-team-fill text-orange-500"></i>
                    <div>
                      <div className="font-semibold">Safety Staffing</div>
                      <div className="text-xs text-gray-500">On-site safety professionals</div>
                    </div>
                  </div>
                </Link>
                <Link 
                  to="/industrial-hygiene" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-center space-x-3">
                    <i className="ri-microscope-fill text-orange-500"></i>
                    <div>
                      <div className="font-semibold">Industrial Hygiene</div>
                      <div className="text-xs text-gray-500">Environmental monitoring</div>
                    </div>
                  </div>
                </Link>
                <Link 
                  to="/osha-consulting" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-center space-x-3">
                    <i className="ri-shield-check-fill text-orange-500"></i>
                    <div>
                      <div className="font-semibold">OSHA Consulting</div>
                      <div className="text-xs text-gray-500">Compliance & auditing</div>
                    </div>
                  </div>
                </Link>
                <Link 
                  to="/osha-training" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-center space-x-3">
                    <i className="ri-graduation-cap-fill text-orange-500"></i>
                    <div>
                      <div className="font-semibold">OSHA Training</div>
                      <div className="text-xs text-gray-500">Safety education programs</div>
                    </div>
                  </div>
                </Link>
                <Link 
                  to="/coating-inspection" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-l-4 border-transparent hover:border-orange-500"
                >
                  <div className="flex items-center space-x-3">
                    <i className="ri-brush-fill text-orange-500"></i>
                    <div>
                      <div className="font-semibold">Coating Inspection Services</div>
                      <div className="text-xs text-gray-500">Industrial coating solutions</div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            {/* About Dropdown */}
            <div className="relative group">
              <button 
                className="flex items-center space-x-1 font-medium text-gray-700 hover:text-orange-600 transition-colors"
                onMouseEnter={() => setActiveDropdown('about')}
              >
                <span>About</span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              <div 
                className={`absolute top-full left-0 w-56 bg-white shadow-xl rounded-lg border border-gray-200 py-2 transition-all duration-200 ${
                  activeDropdown === 'about' ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}
                onMouseEnter={() => setActiveDropdown('about')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link 
                  to="/mission-vision" 
                  className="block px-4 py-3 text-gray-700 hover:bgorange-50 hover:text-orange-600 transition-colors"
                >
                  Mission & Vision
                </Link>
                <Link 
                  to="/core-values" 
                  className="block px-4 py-3 text-gray-700 hover:bgorange-50 hover:text-orange-600 transition-colors"
                >
                  Core Values
                </Link>
                <Link 
                  to="/meet-team" 
                  className="block px-4 py-3 text-gray-700 hover:bgorange-50 hover:text-orange-600 transition-colors"
                >
                  Meet Our Team
                </Link>
                <Link 
                  to="/testimonials" 
                  className="block px-4 py-3 text-gray-700 hover:bgorange-50 hover:text-orange-600 transition-colors"
                >
                  Testimonials
                </Link>
              </div>
            </div>

            <Link 
              to="/contact" 
              className={`font-medium transition-colors hover:text-orange-600 ${
                isActive('/contact') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Contact
            </Link>

            <Link 
              to="/blog" 
              className={`font-medium transition-colors hover:text-orange-600 ${
                isActive('/blog') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              News
            </Link>

            <Link 
              to="/careers" 
              className={`font-medium transition-colors hover:text-orange-600 ${
                isActive('/careers') ? 'text-orange-600' : 'text-gray-700'
              }`}
            >
              Careers
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link 
              to="/contact" 
              className="safety-button whitespace-nowrap"
            >
              Get Free Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMenu}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-orange-600 transition-colors"
          >
            <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="space-y-2">
              <Link 
                to="/" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/safety-staffing" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Safety Staffing
              </Link>
              <Link 
                to="/industrial-hygiene" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Industrial Hygiene
              </Link>
              <Link 
                to="/osha-consulting" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                OSHA Consulting
              </Link>
              <Link 
                to="/osha-training" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                OSHA Training
              </Link>
              <Link 
                to="/coating-inspection" 
                className="block py-2 text-gray-700 hover:text-orange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Coating Inspection Services
              </Link>
              <Link 
                to="/mission-vision" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Mission & Vision
              </Link>
              <Link 
                to="/core-values" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Core Values
              </Link>
              <Link 
                to="/meet-team" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Meet Our Team
              </Link>
              <Link 
                to="/testimonials" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Testimonials
              </Link>
              <Link 
                to="/contact" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/blog" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                News
              </Link>
              <Link 
                to="/careers" 
                className="block py-2 text-gray-700 hover:textorange-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Careers
              </Link>
              <div className="pt-4">
                <Link 
                  to="/contact" 
                  className="safety-button block text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Free Consultation
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
